import { useState } from "react";

import { useLocale, useT } from "../../shared/i18n";
import { apiErrorOf, type ApiClient } from "../../shared/api/client";
import { Banner, Button, Field, Input, Sheet } from "../../shared/ui";
import { ExportLinks, ExportNoticeBanner } from "./ExportLinks";
import { fill, formatDate, projectedPurge } from "./accountFormat";
import { useExports, type ExportsApi } from "./useExports";

const CONFIRMATION = "DELETE";

export type DeletionWizardApi = ExportsApi & Pick<ApiClient, "requestDeletion">;

type Step = 1 | 2 | 3;
type Outcome =
  | { kind: "done"; purgeAt: string; links: number }
  | { kind: "not_deletable" }
  | { kind: "failed" };

/** Three steps: offer an export, review live links, type DELETE. */
export function DeletionWizard({
  open,
  onOpenChange,
  api,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  api: DeletionWizardApi;
  /** Called once the owner acknowledges a scheduled deletion. */
  onDone: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const exports = useExports(api);
  const [chosenStep, setStep] = useState<Step | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  // The first step depends on whether a link already exists.
  const loaded = exports.items !== null;
  const hasLinks = (exports.items?.length ?? 0) > 0;
  // Pin the first step once the links are known so revoking them all does
  // not bounce the owner back to the export offer.
  if (loaded && chosenStep === null) setStep(hasLinks ? 2 : 1);
  const step = chosenStep;

  const submit = async () => {
    setBusy(true);
    try {
      const result = await api.requestDeletion(typed);
      setOutcome({
        kind: "done",
        purgeAt: result.purge_at,
        links: result.active_export_links,
      });
    } catch (cause: unknown) {
      setOutcome(
        apiErrorOf(cause)?.code === "account_not_deletable"
          ? { kind: "not_deletable" }
          : { kind: "failed" },
      );
    } finally {
      setBusy(false);
    }
  };

  const done = outcome?.kind === "done" ? outcome : null;
  const current = step ?? 1;
  const cancel = (
    <Button
      variant="secondary"
      onClick={() => {
        onOpenChange(false);
      }}
    >
      {t("account.dialog.cancel")}
    </Button>
  );
  const footer = done ? (
    <Button onClick={onDone}>{t("account.delete.finish")}</Button>
  ) : step === null ? (
    cancel
  ) : current === 1 ? (
    <>
      {cancel}
      <Button
        onClick={() => {
          setStep(3);
        }}
      >
        {exports.notice?.kind === "requested"
          ? t("account.delete.continue")
          : t("account.delete.skip")}
      </Button>
    </>
  ) : current === 2 ? (
    <>
      {cancel}
      <Button
        onClick={() => {
          setStep(3);
        }}
      >
        {t("account.delete.continue")}
      </Button>
    </>
  ) : (
    <>
      {cancel}
      <Button
        variant="destructive"
        disabled={typed !== CONFIRMATION || busy}
        loading={busy}
        onClick={() => {
          void submit();
        }}
      >
        {t("account.delete.submit")}
      </Button>
    </>
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        // The session is gone once deletion is scheduled; closing must leave.
        if (!next && done) onDone();
        else onOpenChange(next);
      }}
      title={t("account.delete.title")}
      description={
        done || step === null ? undefined : (
          <span>{fill(t("account.delete.step"), { n: current })}</span>
        )
      }
      footer={footer}
    >
      <div className="flex flex-col gap-3">
        {done ? (
          <>
            <Banner tone="success">
              {fill(t("account.delete.done"), {
                when: formatDate(locale, done.purgeAt),
              })}
            </Banner>
            <p className="text-body text-fg">
              {fill(t("account.delete.linksRemain"), { count: done.links })}
            </p>
          </>
        ) : step === null ? null : current === 1 ? (
          <>
            <p className="text-body text-fg">
              {t("account.delete.offerExport")}
            </p>
            {exports.notice && <ExportNoticeBanner notice={exports.notice} />}
            <Button
              variant="secondary"
              disabled={exports.busy}
              className="self-start"
              onClick={() => {
                void exports.request();
              }}
            >
              {t("account.export.button")}
            </Button>
          </>
        ) : current === 2 ? (
          <>
            <p className="text-body text-fg">
              {t("account.delete.linksIntro")}
            </p>
            {exports.notice && <ExportNoticeBanner notice={exports.notice} />}
            <ExportLinks
              items={exports.items ?? []}
              busy={exports.busy}
              onRevoke={(id) => {
                void exports.revokeOne(id);
              }}
              onRevokeAll={() => {
                void exports.revokeAll();
              }}
            />
          </>
        ) : (
          <>
            <p className="text-body text-fg">
              {fill(t("account.delete.finalIntro"), {
                when: formatDate(locale, projectedPurge()),
              })}
            </p>
            {outcome?.kind === "not_deletable" && (
              <Banner tone="error">{t("account.delete.notDeletable")}</Banner>
            )}
            {outcome?.kind === "failed" && (
              <Banner tone="error">{t("account.delete.failed")}</Banner>
            )}
            <Field label={t("account.delete.confirmLabel")}>
              {(props) => (
                <Input
                  {...props}
                  value={typed}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  onChange={(event) => {
                    setTyped(event.target.value);
                  }}
                />
              )}
            </Field>
          </>
        )}
      </div>
    </Sheet>
  );
}
